import { ReactNode } from 'react'
import { Box, Stack, useTheme } from '@mui/material'
import { ResponsiveBar, BarSvgProps, BarTooltipProps } from '@nivo/bar'
import constants from './chart-constants'
import TooltipCard from './TooltipCard'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DataType = Record<string, any>

export type BarChartProps = {
  xKey?: string // deprecating - used indexBy
  keys: string[]
  height?: number | string
  heading?: ReactNode
  noData?: boolean
  isLoading?: boolean
  isError?: boolean
  tooltipContent?: (props: BarTooltipProps<DataType>) => ReactNode
  // These four are to make small changes instead of passing the axisBottom or axisLeft
  // object to change those, and to match what we are doing for the ScatterPlotChart
  xFormat?: string | ((value: string) => string)
  xLegend?: string
  yFormat?: string | ((value: string) => string)
  yLegend?: string
} & Omit<BarSvgProps<DataType>, 'height' | 'width'>

export default function BarChart({
  data,
  xKey,
  indexBy,
  keys,
  height = 420,
  heading,
  noData,
  isLoading,
  isError,
  axisBottom,
  axisLeft,
  margin,
  tooltip,
  tooltipContent,
  xFormat,
  xLegend,
  yFormat,
  yLegend,
  layout = 'vertical', // vertical is the default, but if we don't set it explicitly, the animation grows the bars horizontally
  ...props
}: BarChartProps) {
  const theme = useTheme()
  const noDataProps =
    data.length === 0 || noData
      ? {
          enableGridY: false,
          maxValue: 1,
          markers: [
            {
              axis: layout === 'vertical' ? ('y' as const) : ('x' as const),
              value: 0.5,
              lineStyle: {
                stroke: 'transparent',
              },
              legend: isLoading
                ? 'Loading...'
                : isError
                ? 'Failed to Load Data'
                : 'No Data',
              textStyle: {
                fill: theme.palette.neutral.main,
              },
              legendPosition:
                layout === 'vertical' ? ('top' as const) : ('right' as const),
              legendOrientation: 'horizontal' as const,
              legendOffsetY: layout === 'vertical' ? 15 : 0,
              legendOffsetX: layout === 'vertical' ? 0 : 240,
            },
          ],
        }
      : {}

  const marginBottom = margin?.bottom
    ? heading
      ? margin.bottom + 30 // 30 is the height of the heading
      : margin.bottom
    : undefined
  const updatedMargin = margin
    ? {
        ...margin,
        bottom: marginBottom,
      }
    : undefined

  return (
    <Box sx={{ height }}>
      {heading && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {heading}
        </Stack>
      )}
      {/* ResponsiveBar needs to be wrapped by a container with a set height */}
      <ResponsiveBar
        data={data}
        indexBy={indexBy || xKey}
        indexScale={{ type: 'band', round: false }}
        keys={keys}
        minValue={0}
        axisBottom={{
          format: xFormat,
          legend: xLegend,
          legendPosition: 'middle',
          ...axisBottom,
        }}
        axisLeft={{
          format: yFormat,
          legend: yLegend,
          legendPosition: 'middle',
          legendOffset: -50,
          tickValues: data.length === 0 || noData ? [0, 1] : undefined,
          ...axisLeft,
        }}
        labelTextColor={(d) => {
          if (d.color) return theme.palette.getContrastText(d.color)
          return theme.palette.text.primary
        }}
        layout={layout}
        margin={{
          top: constants.MARGIN_TOP,
          right: constants.MARGIN_RIGHT,
          bottom: xLegend
            ? constants.MARGIN_BOTTOM_WITH_LEGEND
            : constants.MARGIN_BOTTOM,
          left: yLegend
            ? constants.MARGIN_LEFT_WITH_LEGEND
            : constants.MARGIN_LEFT,
          ...updatedMargin,
        }}
        padding={0.7}
        tooltip={
          tooltipContent
            ? (props) => <TooltipCard>{tooltipContent(props)}</TooltipCard>
            : tooltip
        }
        theme={{
          axis: {
            domain: {
              line: {
                stroke: '#777777',
              },
            },
            legend: {
              text: {
                fontSize: 14,
                fill: '#d2d2d2',
              },
            },
            ticks: {
              text: {
                fill: theme.palette.text.secondary,
              },
            },
          },
          grid: {
            line: {
              stroke: theme.palette.divider,
            },
          },
        }}
        {...noDataProps}
        {...props}
      />
    </Box>
  )
}
